/**
 * Permission matrix translation. Two pure functions:
 *
 *   matrixToPermissionStrings(matrix)
 *     Translates a Role.permissions matrix into the set of legacy
 *     permission strings ('charger:read', 'session:stop', etc.)
 *     the existing Permission table consumes. Used by the Phase
 *     2.4 middleware to populate the JWT's permissions[] claim
 *     so Pattern A sites (requirePermission(req, reply, 'X:Y'))
 *     keep working unchanged after the matrix becomes the
 *     authority.
 *
 *   permissionStringsToMatrix(strings)
 *     Reverse direction. Used by the Phase 2.3 seed to derive
 *     system-role matrices from the legacy RolePermissions[role]
 *     map, bit-for-bit. Round-trips: matrixToPermissionStrings
 *     of permissionStringsToMatrix(strings) yields the same set
 *     as `strings` (modulo set-not-array semantics).
 *
 * These functions encode the irregular mappings the v2 codebase
 * accumulated:
 *
 *   Charger.read/write/delete    -> charger:read/write/delete
 *   Command.read                  -> charger:command (NOT command:*)
 *   Station.read/write            -> location:read/write
 *   Booking.read                  -> session:read
 *   Booking.delete                -> session:stop (NOT session:delete)
 *   Log.read                      -> audit:read
 *   Reports.read                  -> report:read (singular)
 *   User/Tariff/RFID/Billing/
 *     Tenant/Coupon/Wallet/
 *     Gamification/Connect/
 *     Dashboard/Role             -> regular `${lowercase}:read|write|delete`
 *
 * `system:admin` is NOT represented in the matrix. The Phase 2.4
 * middleware handles super_admin bypass via the system_role flag
 * on the Role doc.
 *
 * Resources without a corresponding legacy permission string
 * (e.g. Dashboard, Gamification, Coupon, Wallet, Connect, Role)
 * still expand into matching permission strings via the regular
 * `${lowercase}:read|write|delete` rule. The new endpoints
 * Phase 2.4 wires up will use those strings.
 */
/**
 * Permission resources in the matrix. Mirrors v1's resource list
 * plus v2-specific additions (Gamification, Coupon, Wallet,
 * Connect) plus two more (Billing, Tenant) needed to bit-for-bit
 * preserve the legacy RolePermissions[role] string set.
 */
export const PERMISSION_RESOURCES = [
    'Station', // Legacy alias — maps to location:*. Kept for any
    // production Role doc still referencing it; new
    // matrices should use 'Location' below.
    'Charger',
    'User',
    'Role',
    'Command', // OCPP commands gate (charger:command lives here)
    'Log', // Audit + OCPP message logs (audit:read lives here)
    'Booking', // Legacy alias — maps to session:*. delete=true emits
    // session:stop. Kept for compat; new matrices should
    // use 'Session' below (which maps to session:* via
    // the default rule, no special delete→stop mapping).
    'Tariff',
    'Dashboard',
    'RFID',
    'Reports',
    'Gamification',
    'Coupon',
    'Wallet',
    'Connect', // Stripe Connect
    'Billing', // billing:read / billing:write
    'Tenant', // tenant:read / tenant:write (super_admin scope)
    'Group', // Customer Groups (Phase A) — segments of driver users
    'Fleet', // Phase 2 fleet platform — fleet:read/write/delete
    'AgentPayout', // Agent commission Phase 1b — agentpayout:read/write
    // Phase 7g.4a additions — modern names that read clearly in the
    // sales_agent seed and any future matrix that grants on
    // sessions / locations / agents directly. Coexist with the
    // legacy Booking/Station aliases above (which are kept so any
    // production Role doc referencing them keeps validating). The
    // matrix-to-permission-string translator below maps Session and
    // Location to identical legacy strings as Booking and Station
    // respectively, so granting either resource produces the same
    // permission claim — pick the more readable name in new code.
    'Session', // Phase 7g.4a — sales_agent reads sessions on their
    // assigned chargers. session:read via default mapping.
    'Location', // Phase 7g.4a — sales_agent reads locations their
    // chargers live at. location:read|write via map below.
    'Agent', // Phase 7g.4a — sales_agent reads their own Agent
    // rolodex row (drives the SP dashboard identity card
    // + Connect status). agent:read|write via map below;
    // PATCH whitelist enforced at the route layer, not
    // the matrix.
    'AdminAgent', // Admin EVIE agent access — a DEDICATED gate, granted
    // distinctly from Dashboard so agent access is assignable to
    // roles on its own. adminagent:read via the map below; the
    // /admin-assistant endpoint gates on AdminAgent.read.
    'Announcement', // Admin-managed dismissible landing popups —
    // announcement:read|write via the map below; no delete
    // (soft-disable via is_active only).
    'Alert', // Operator alert queue — alert:read|write. Added
    // 2026-09-24. The whole alerts surface was gated on
    // Tenant:read / Tenant:update, and no seeded matrix
    // carries a Tenant row, so it was reachable only by
    // super_admin's bypass and had no nav leaf. Alerts
    // are an operator's daily work, not a platform-owner
    // concern, so they get a resource of their own rather
    // than riding the broadest one in the map.
    'Points', // Points economy — points:write gates a manual balance
    // adjustment (POST /admin/users/:userId/gift-points).
    // Added 2026-09-25 (Warren's F ruling). It rode on
    // User:update, which operator gained on 2026-09-24 for
    // driver support and every Site template held for
    // resident management — so a building admin could
    // credit THEMSELVES up to 100,000 points per call,
    // repeatably. Points are money-adjacent (they gate
    // pay-to-use and rank), so adjusting them is its own
    // grant and no third party holds it.
];
/**
 * Per-resource mapping. Resources missing from this table fall
 * through to a default rule (`${lowercase}:read|write|delete`,
 * all three actions exposed). Adding a row here lets us pin
 * irregular legacy strings or restrict which actions a resource
 * accepts.
 */
const RESOURCE_MAP = {
    Charger: {
        base: 'charger',
        hasRead: true, hasWrite: true, hasDelete: true,
    },
    Command: {
        base: 'command',
        // The legacy string is 'charger:command', not 'command:read'.
        // Read on the Command resource gates the OCPP command surface
        // on chargers; the bit-for-bit string is irregular.
        readString: 'charger:command',
        hasRead: true, hasWrite: false, hasDelete: false,
    },
    Station: {
        base: 'location',
        hasRead: true, hasWrite: true, hasDelete: false,
    },
    User: {
        base: 'user',
        hasRead: true, hasWrite: true, hasDelete: true,
    },
    Booking: {
        base: 'session',
        // delete=true on Booking maps to 'session:stop' (the legacy
        // string for forcefully ending a session). Not 'session:delete'.
        deleteString: 'session:stop',
        hasRead: true, hasWrite: false, hasDelete: true,
    },
    Tariff: {
        base: 'tariff',
        hasRead: true, hasWrite: true, hasDelete: false,
    },
    RFID: {
        base: 'rfid',
        hasRead: true, hasWrite: true, hasDelete: false,
    },
    Reports: {
        // Legacy string is the singular 'report:read'.
        base: 'report',
        hasRead: true, hasWrite: false, hasDelete: false,
    },
    Billing: {
        base: 'billing',
        hasRead: true, hasWrite: true, hasDelete: false,
    },
    Tenant: {
        base: 'tenant',
        hasRead: true, hasWrite: true, hasDelete: false,
    },
    Log: {
        // Legacy string is 'audit:read'. Audit log is the read surface
        // here.
        base: 'audit',
        hasRead: true, hasWrite: false, hasDelete: false,
    },
    // Resources without legacy permission strings yet. These will
    // grow legacy-string consumers as Phase 2.4 wires endpoints.
    // Default mapping (`${lowercase}:read|write|delete`) applies via
    // the fallback below; entries here just declare the resource
    // shape for completeness.
    Role: {
        base: 'role',
        hasRead: true, hasWrite: true, hasDelete: true,
    },
    Dashboard: {
        base: 'dashboard',
        hasRead: true, hasWrite: false, hasDelete: false,
    },
    Gamification: {
        base: 'gamification',
        hasRead: true, hasWrite: true, hasDelete: false,
    },
    Coupon: {
        base: 'coupon',
        hasRead: true, hasWrite: true, hasDelete: true,
    },
    Wallet: {
        base: 'wallet',
        hasRead: true, hasWrite: true, hasDelete: false,
    },
    Connect: {
        base: 'connect',
        hasRead: true, hasWrite: true, hasDelete: false,
    },
    Group: {
        // Customer Groups (Phase A). Regular `group:read|write|delete`
        // — no irregular legacy strings to pin. Full CRUD exposed in
        // the matrix; row defaults to all-false on tenant clones until
        // the seed populates per-system-role bits.
        base: 'group',
        hasRead: true, hasWrite: true, hasDelete: true,
    },
    AgentPayout: {
        // Agent commission Phase 1b. Read = view earnings rows (admin +
        // the sales_agent role's own scoped view); write = mark as
        // adjusted / requeue (admin-only, ops surface in Phase 5). No
        // delete — payouts are append-only for audit; refund/reversal
        // updates a status field rather than removing the row.
        base: 'agentpayout',
        hasRead: true, hasWrite: true, hasDelete: false,
    },
    Alert: {
        // alert:read gates the list, the detail and the operator Alerts
        // nav leaf; alert:write gates acknowledge, resolve and the
        // re-fireable /notify send. No delete — an alert is resolved, never
        // removed, so the queue stays auditable. See the PERMISSION_RESOURCES
        // entry for why this is not Tenant.
        base: 'alert',
        hasRead: true, hasWrite: true, hasDelete: false,
    },
    Points: {
        // points:write gates a manual balance adjustment. No read of its own
        // (a driver's balance is shown on surfaces with their own gates) and
        // no delete (the ledger is append-only; a correction is a negative
        // adjustment, never an erasure). See the PERMISSION_RESOURCES entry.
        base: 'points',
        hasRead: false, hasWrite: true, hasDelete: false,
    },
    Fleet: {
        // Phase 2 fleet platform — fleet:read/write/delete. Until this row
        // existed, Fleet was absent from RESOURCE_MAP, so
        // matrixToPermissionStrings DROPPED every Fleet matrix grant: that
        // made requireMatrixPermission(Fleet, *) unsatisfiable (permissionStringFor
        // → null → deny) AND left the fleets nav un-capability-gateable (hence
        // the prior tier-gate). Phase 1a adds it so fleet:read is producible —
        // read gates the Fleets nav + the GET /admin/fleets list
        // (fleet_admin lists their assigned fleets via requireFleetScope
        // downstream); write/delete are reserved for later fleet-ops grants.
        base: 'fleet',
        hasRead: true, hasWrite: true, hasDelete: true,
    },
    // Phase 7g.4a — modern resource names. Each produces the same
    // legacy permission string as its alias (Session→session:*,
    // Location→location:*, Agent→agent:*). hasWrite/hasDelete shape
    // matches the closest existing convention:
    //   - Session: read-only in v1 (no operator write surface on
    //     sessions; the legacy Booking alias has hasDelete:true for
    //     the special session:stop string, but Session uses the
    //     plain default-mapping shape so no special-case is needed)
    //   - Location: read + write + delete. delete gates the operator-facing
    //     soft-delete-with-cascade route distinctly from write (parallel to
    //     Charger.delete); locations still soft-delete via the active flag, the
    //     delete bit just authorizes that action separately from edit.
    //   - Agent: read + write but no delete (Agent.active soft-delete
    //     is the only "removal" path; PATCH /admin/agents/:id is the
    //     write surface; route-layer whitelist gates which fields
    //     the SP-self caller can update)
    Session: {
        base: 'session',
        hasRead: true, hasWrite: false, hasDelete: false,
    },
    Location: {
        // location:read|write|delete. delete added so an operator-facing Location
        // delete (soft-delete with charger/circuit cascade) is gated distinctly
        // from edit, parallel to Charger.delete. super_admin bypasses; everyone
        // else needs an explicit location:delete grant.
        base: 'location',
        hasRead: true, hasWrite: true, hasDelete: true,
    },
    Agent: {
        base: 'agent',
        hasRead: true, hasWrite: true, hasDelete: false,
    },
    Announcement: {
        // Admin-managed landing popups. No delete — soft-disable via is_active.
        base: 'announcement',
        hasRead: true, hasWrite: true, hasDelete: false,
    },
    AdminAgent: {
        // Admin EVIE agent. read = "may use the admin agent" -> adminagent:read.
        // Granted distinctly from Dashboard so agent access is assignable on its
        // own; the /admin-assistant route gates on
        // requireMatrixPermission('AdminAgent', 'read').
        base: 'adminagent',
        hasRead: true, hasWrite: false, hasDelete: false,
    },
};
/**
 * THE SITE SURFACE: one matrix for building_admin, charger_owner and
 * property_manager (Warren's F rulings, 2026-09-25). They render one authored
 * Site menu; their AccessAssignment is what separates them.
 *
 * It lives HERE, not in scripts/seed-rbac-templates.ts, so that the seed and
 * apps/admin's personaMenu.test.ts read the same data. The test used to
 * hand-copy the seeded matrices and drifted: after the Site templates were
 * trimmed, its building_admin row still granted Tariff, Coupon and User in
 * full, so "nothing a Site persona sees is an item that 403s" was being
 * checked against a seed that no longer existed. See the seed's
 * SITE_PERMISSIONS docblock for what each grant is for and what was removed.
 *
 * The two write rows are narrowed BY THE ROUTES, not by this matrix: create
 * and update collapse onto one `<base>:write` string, so a matrix cannot say
 * "update this field but create nothing".
 *   Station  read+update  rate + resident toggle (PUT /locations/:id)
 *   Charger  read+update  display name + photo (PATCH /chargers/:id)
 *
 * NO BILLING, on any of the three (Warren, 2026-09-30):
 *   (f) "Property managers and Billing access. No. Billing shows tenant-level
 *       money that isn't theirs. Earnings already gives them their own sites'
 *       charging sales and costs."
 *   (c) "Earnings shows charging sales, electricity costs and held electricity
 *       as separate lines. Personal payouts stay hidden until each payout
 *       records who received it."
 * No Site surface uses it. Since e9c8c385 Earnings and My Sites read
 * /dashboard/site-comparison (Dashboard:read) and the bank card reads
 * /locations/:id/connect/status (Location:read). All Billing:read did for a
 * Site account was open /money, /billing/revenue and /billing/payouts by
 * typed URL, which list per-session Owner Payout and Platform Fee at their
 * sites - on a co-held site, another holder's payout - the opposite of (c),
 * and GET /billing/transactions by direct call (no Site screen uses it).
 * Removed from the one shared matrix rather than from property_manager alone,
 * so the three templates stay one matrix. Revisit only with payee-keyed
 * payouts: docs/tickets/earnings-by-payee.md.
 */
export const SITE_TEMPLATE_PERMISSIONS = [
    { resource: 'Station', create: false, read: true, update: true, delete: false },
    { resource: 'Charger', create: false, read: true, update: true, delete: false },
    { resource: 'Booking', create: false, read: true, update: false, delete: false },
    { resource: 'Reports', create: false, read: true, update: false, delete: false },
    { resource: 'Dashboard', create: false, read: true, update: false, delete: false },
    { resource: 'Command', create: false, read: true, update: false, delete: false },
    { resource: 'AdminAgent', create: false, read: true, update: false, delete: false },
];
/**
 * Translate a permissions matrix into the set of legacy permission
 * strings the v2 codebase consumes. Idempotent on repeat translation.
 * Returns a deduped, sorted array.
 */
export function matrixToPermissionStrings(matrix) {
    const out = new Set();
    for (const entry of matrix) {
        const map = RESOURCE_MAP[entry.resource];
        if (!map)
            continue;
        if (entry.read && map.hasRead) {
            out.add(map.readString ?? `${map.base}:read`);
        }
        if ((entry.create || entry.update) && map.hasWrite) {
            out.add(map.writeString ?? `${map.base}:write`);
        }
        if (entry.delete && map.hasDelete) {
            out.add(map.deleteString ?? `${map.base}:delete`);
        }
    }
    return [...out].sort();
}
/**
 * Reverse direction. Takes a legacy-string list (e.g. from the
 * existing RolePermissions[role] map) and produces a matrix
 * entry list that translates back to the same string set. Used
 * by the Phase 2.3 seed to derive system-role matrices.
 *
 * Strings without a matching resource are silently dropped
 * (e.g. 'system:admin', which lives outside the matrix). Pass
 * the system_role flag separately on the Role doc for those.
 */
export function permissionStringsToMatrix(strings) {
    // Build reverse lookup: string -> { resource, action }. Cache
    // to avoid re-deriving on every call.
    const reverseLookup = buildReverseLookup();
    const byResource = new Map();
    for (const s of strings) {
        const hit = reverseLookup.get(s);
        if (!hit)
            continue;
        let entry = byResource.get(hit.resource);
        if (!entry) {
            entry = {
                resource: hit.resource,
                create: false, read: false, update: false, delete: false,
            };
            byResource.set(hit.resource, entry);
        }
        if (hit.action === 'read')
            entry.read = true;
        if (hit.action === 'write') {
            // Legacy `:write` is create+update combined.
            entry.create = true;
            entry.update = true;
        }
        if (hit.action === 'delete')
            entry.delete = true;
    }
    return [...byResource.values()];
}
let _reverseLookup = null;
function buildReverseLookup() {
    if (_reverseLookup)
        return _reverseLookup;
    const m = new Map();
    for (const [resource, mapping] of Object.entries(RESOURCE_MAP)) {
        if (mapping.hasRead) {
            m.set(mapping.readString ?? `${mapping.base}:read`, { resource, action: 'read' });
        }
        if (mapping.hasWrite) {
            m.set(mapping.writeString ?? `${mapping.base}:write`, { resource, action: 'write' });
        }
        if (mapping.hasDelete) {
            m.set(mapping.deleteString ?? `${mapping.base}:delete`, { resource, action: 'delete' });
        }
    }
    _reverseLookup = m;
    return m;
}
//# sourceMappingURL=rbac.js.map